import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTree, MatTreeNodeDef, MatTreeNode, MatTreeNodeToggle, MatNestedTreeNode, MatTreeNodeOutlet } from '@angular/material/tree';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { groupBy } from 'lodash-es';
import { ComponentStatusService } from './../../services';
import { OrderUtilityService } from './../../../../services';
import { ComponentStatusNode } from '../../models/interfaces';
import { ComponentStatus } from './../../models/enums';
import { ComponentStatusModel, ComponentStatusModelGroup } from './../../models';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';

@Component({
  selector: 'insight-component-status',
  templateUrl: './component-status.component.html',
  styleUrls: ['./component-status.component.scss', '../../scss/operational-dashboard-shared.scss'],
  imports: [
    RouterLink,
    MatButton,
    MatIcon,
    LoadingSpinnerComponent,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodeToggle,
    MatIconButton,
    MatTooltip,
    NgClass,
    MatNestedTreeNode,
    MatTreeNodeOutlet
  ]
})
export class ComponentStatusComponent implements OnInit, OnDestroy {
  public isLoading = true;
  public didError = false;
  public componentStatus = ComponentStatus;
  public dataSource: ComponentStatusNode[] = [];
  public childrenAccessor = (node: ComponentStatusNode) => node.children ?? [];

  public componentStatusRawData: ComponentStatusModelGroup[] = [];

  @ViewChild(MatTree) private tree: MatTree<ComponentStatusNode>;
  private $unsubscribeSubject = new Subject();

  constructor(
    private orderUtilityService: OrderUtilityService,
    private componentStatusService: ComponentStatusService,
    private toastr: ToastrService) { }

  ngOnInit() {
    this.componentStatusService.getComponentStatuses()
      .pipe(takeUntil(this.$unsubscribeSubject))
      .subscribe((statuses: ComponentStatusModelGroup[]) => {
        this.componentStatusRawData = statuses;
        this.dataSource = this.createOrderTypeNodes(statuses);
        this.isLoading = false;
      }, (error: any) => {
        this.isLoading = false;
        this.didError = true;
        this.toastr.error('Failed to retrieve component statuses', 'Server Error: ');
      });
  }

  ngOnDestroy() {
    this.$unsubscribeSubject.next();
    this.$unsubscribeSubject.complete();
  }

  public collapseAll() {
    this.tree.collapseAll();
  }

  public expandAll() {
    this.tree.expandAll();
  }

  public hasChild(notUsed: number, node: ComponentStatusNode): boolean {
    return !!node.children && node.children.length > 0;
  };

  private createOrderTypeNodes(componentStatuses: ComponentStatusModelGroup[]): ComponentStatusNode[] {
    const nodes: ComponentStatusNode[] = [];
    const orderTypeRecord: Record<string, ComponentStatusModelGroup[]> = groupBy(componentStatuses, 'orderTypeMediaType.item1');
    Object.keys(orderTypeRecord).forEach(key => {
      const node: ComponentStatusNode = {
        name: this.orderUtilityService.mapOrderOrderType(parseInt(key, 10)),
        children: this.createMediaTypeNodes(groupBy(orderTypeRecord[key], 'orderTypeMediaType.item2'))
      };
      nodes.push(node);
    });

    return nodes;
  }

  private createMediaTypeNodes(componentStatusMedia: Record<string, ComponentStatusModelGroup[]>): ComponentStatusNode[] {
    const nodes: ComponentStatusNode[] = [];
    Object.keys(componentStatusMedia).forEach(key => {
      const node: ComponentStatusNode = {
        name: this.orderUtilityService.mapOrderMediaType(parseInt(key, 10)),
        children: this.createStatusTypeNodes(groupBy(componentStatusMedia[key], 'componentName'))
      };
      nodes.push(node);
    });

    return nodes;
  }

  private createStatusTypeNodes(componentStatusStatuses: Record<string, ComponentStatusModelGroup[]>): ComponentStatusNode[] {
    const nodes: ComponentStatusNode[] = [];
    Object.keys(componentStatusStatuses).forEach(key => {
      const node: ComponentStatusNode = {
        name: key,
        status: this.evaluateStatusNodeStatus(componentStatusStatuses[key][0].componentStatusModels),
        children: this.createComponentStatusModelNodes(componentStatusStatuses[key][0].componentStatusModels)
      };
      nodes.push(node);
    });

    return nodes;
  }

  private createComponentStatusModelNodes(componentStatusModels: ComponentStatusModel[]): ComponentStatusNode[] {
    const nodes: ComponentStatusNode[] = [];
    componentStatusModels.forEach(model => {
      const node: ComponentStatusNode = {
        name: model.name.trim(),
        description: model.description.trim(),
        status: model.status
      };
      nodes.push(node);
    });

    return nodes;
  }

  private evaluateStatusNodeStatus(models: ComponentStatusModel[]): ComponentStatus {
    let status = ComponentStatus.Ok;

    for (const model of models) {
      if (model.status === ComponentStatus.Error) {
        status = ComponentStatus.Error;
        return status;
      } else if (model.status === ComponentStatus.Warning) {
        status = ComponentStatus.Warning;
      }
    }

    return status;
  }
}
